import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// Import our business logic services
import { StateManagerService } from './services/StateManagerService';
import { AnalyticsService } from './services/AnalyticsService';

// Import types
import { GameState, GameMode, UserSettings, DailyState, PersistentState } from './types/GameTypes';

// Import configuration
import { GAME_CONFIG } from './config';
import { MODAL_TYPES } from './types/AnalyticsConstants';
import { DEFAULT_WORD_PACK } from './data/wordpacks';

// Import components
import HowToPlay from './components/HowToPlay';
import Settings from './components/Settings';
import Results from './components/Results';
import GameBoard from './components/GameBoard';
import StreakDisplay from './components/StreakDisplay';
import Footer from './components/Footer';
import WordCelebration from './components/WordCelebration';

function App() {
  // Initialize services
  const [stateManager] = useState(() => new StateManagerService());
  const [analyticsService] = useState(() => AnalyticsService.getInstance());
  
  // Game state
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [gameMode, setGameMode] = useState<GameMode>('daily');
  
  // UI state
  const [showResults, setShowResults] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Accessibility state
  const [announcement, setAnnouncement] = useState<string>('');
  const [gameStatusAnnouncement, setGameStatusAnnouncement] = useState<string>('');
  const previousGameStateRef = useRef<GameState | null>(null);
  
  // Celebration state
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationIntensity, setCelebrationIntensity] = useState(0);
  
  // User settings
  const [settings, setSettings] = useState<UserSettings>(() => {
    try {
      return stateManager.loadSettings();
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings. Using defaults.');
      
      // Track error for analytics
      stateManager.trackError('settings_load_error', 'Failed to load user settings from localStorage', 'using_defaults');
      
      return {
        darkMode: true,
        rstlneMode: true,
        selectedWordPack: DEFAULT_WORD_PACK,
        showGuessedLetters: false,
        animationsEnabled: true
      };
    }
  });
  
  // Daily and persistent state
  const [dailyState, setDailyState] = useState<DailyState | null>(null);
  const [persistentState, setPersistentState] = useState<PersistentState>(() => {
    try {
      return stateManager.loadPersistentState();
    } catch (err) {
      console.error('Failed to load persistent state:', err);
      return { streak: 0, streakScore: 0, lastCompletedDate: null };
    }
  });

  // Session tracking for analytics
  const sessionStartTime = useRef<number>(Date.now());
  const [gamesThisSession, setGamesThisSession] = useState<number>(0);

  // Announce game state changes for screen readers
  useEffect(() => {
    if (!gameState || !previousGameStateRef.current) {
      previousGameStateRef.current = gameState;
      return;
    }

    const prevState = previousGameStateRef.current;
    const newState = gameState;

    // Announce letter reveals
    if (newState.revealedIndices.length > prevState.revealedIndices.length) {
      const newlyRevealed = newState.revealedIndices.filter(i => !prevState.revealedIndices.includes(i));
      if (newlyRevealed.length > 0) {
        const letters = newlyRevealed.map(i => newState.secretWord[i]).join(', ');
        setAnnouncement(`Revealed letter${newlyRevealed.length > 1 ? 's' : ''}: ${letters}`);
      }
    }

    // Announce game completion and trigger celebration
    if (newState.gameOver && !prevState.gameOver) {
      if (newState.gameWon) {
        const score = newState.letterGuessesLeft;
        setGameStatusAnnouncement(`Congratulations! You won! Score: ${score} points. The word was ${newState.secretWord}.`);
        
        // Trigger celebration if animations are enabled
        if (settings.animationsEnabled) {
          // Calculate intensity based on score (higher score = bigger celebration)
          setCelebrationIntensity(score);
          setShowCelebration(true);
        }
      } else {
        setGameStatusAnnouncement(`Game over. The word was ${newState.secretWord}.`);
      }
    }

    previousGameStateRef.current = gameState;
  }, [gameState, settings.animationsEnabled]);

  // Announce mode changes
  useEffect(() => {
    if (gameState && previousGameStateRef.current) {
      setAnnouncement(`Switched to ${gameMode === 'daily' ? 'Daily Challenge' : 'Free Play'} mode`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMode]);

  // Apply dark mode to body element
  useEffect(() => {
    if (settings.darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [settings.darkMode]);

  // Clear announcements after they've been read
  useEffect(() => {
    if (announcement) {
      const timer = setTimeout(() => setAnnouncement(''), 1000);
      return () => clearTimeout(timer);
    }
  }, [announcement]);

  useEffect(() => {
    if (gameStatusAnnouncement) {
      const timer = setTimeout(() => setGameStatusAnnouncement(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [gameStatusAnnouncement]);

  // Initialize game on mount and when game mode changes
  useEffect(() => {
    const initializeAsync = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        await initializeGame();
      } catch (err) {
        console.error('Failed to initialize game:', err);
        setError('Failed to initialize game. Please refresh and try again.');
        
        // Track critical error for analytics
        stateManager.trackError('game_initialization_error', 'Failed to initialize game state or load required data', 'refresh_requested');
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameMode]);

  // Initialize analytics service
  useEffect(() => {
    const initializeAnalytics = async () => {
      try {
        await analyticsService.initialize();
      } catch (error) {
        console.error('Failed to initialize analytics:', error);
        // Don't show error to user - analytics is non-critical
      }
    };
    
    initializeAnalytics();
  }, [analyticsService]);

  // Track user engagement and session metrics
  useEffect(() => {
    const trackEngagement = () => {
      const sessionDuration = Date.now() - sessionStartTime.current;
      const isReturnVisitor = localStorage.getItem('has_visited_before') === 'true';
      
      // Track engagement for sessions longer than 30 seconds with at least 1 game
      if (sessionDuration > 30000 && gamesThisSession > 0) {
        stateManager.trackUserEngagement(sessionDuration, gamesThisSession, isReturnVisitor);
      }
    };

    // Track engagement on page unload
    const handleBeforeUnload = () => {
      trackEngagement();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Set visitor flag
    localStorage.setItem('has_visited_before', 'true');

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      trackEngagement(); // Also track when component unmounts
    };
  }, [gamesThisSession, stateManager]);

  const initializeGame = async () => {
    // Add small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (gameMode === 'daily') {
      initializeDailyMode();
    } else {
      initializeFreePlayMode();
    }
  };

  const initializeDailyMode = () => {
    const loadedDailyState = stateManager.loadDailyState();
    
    if (loadedDailyState && stateManager.isDailyStateFromToday(loadedDailyState)) {
      setDailyState(loadedDailyState);
      
      if (loadedDailyState.completed) {
        setShowResults(true);
        // Set a dummy game state to prevent loading screen
        const dummyGameState = stateManager.startNewGame('daily', undefined, false);
        setGameState(dummyGameState);
        return;
      }
      
      // Try to restore existing game state
      const existingGameState = stateManager.loadGameState('daily');
      if (existingGameState) {
        setGameState(existingGameState);
        return;
      }
    } else {
      // Create new daily state
      const newDailyState = stateManager.createNewDailyState();
      setDailyState(newDailyState);
    }
    
    // Start new daily game
    const newGameState = stateManager.startNewGame('daily');
    setGameState(newGameState);
  };

  const initializeFreePlayMode = () => {
    setShowResults(false);
    const newGameState = stateManager.startNewGame('free-play');
    setGameState(newGameState);
  };

  const handleLetterGuess = (letter: string) => {
    if (!gameState) return;
    
    const newGameState = stateManager.makeLetterGuess(gameState, letter, gameMode);
    setGameState(newGameState);
    
    // Handle game completion in daily mode
    if (gameMode === 'daily' && newGameState.gameOver) {
      setTimeout(() => {
        handleDailyGameCompletion();
      }, GAME_CONFIG.dailyModeTransitionDelay);
    }
  };

  const handleDailyGameCompletion = () => {
    // Cancel any ongoing celebration when transitioning
    setShowCelebration(false);
    
    const updatedDailyState = stateManager.loadDailyState();
    if (updatedDailyState) {
      setDailyState(updatedDailyState);
      
      if (updatedDailyState.completed) {
        const updatedPersistentState = stateManager.loadPersistentState();
        setPersistentState(updatedPersistentState);
        setShowResults(true);
      } else {
        // Start next word
        const nextGameState = stateManager.startNewGame('daily');
        setGameState(nextGameState);
      }
    }
  };

  const handleNewGame = () => {
    // Cancel any ongoing celebration when starting new game
    setShowCelebration(false);
    
    if (gameMode === 'free-play') {
      const newGameState = stateManager.startNewGame('free-play');
      setGameState(newGameState);
      setGamesThisSession(prev => prev + 1);
    } else {
      if (dailyState?.completed) {
        stateManager.trackModalOpen(MODAL_TYPES.RESULTS);
        setShowResults(true);
      } else {
        // Continue daily challenge
        handleDailyGameCompletion();
        setGamesThisSession(prev => prev + 1);
      }
    }
  };

  const handleGameModeSwitch = () => {
    const newMode = gameMode === 'daily' ? 'free-play' : 'daily';
    
    // Track mode switch analytics
    stateManager.trackModeSwitch(gameMode, newMode, 'button');
    
    setGameMode(newMode);
  };

  const handleSettingsChange = (newSettings: Partial<UserSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    stateManager.saveSettings(newSettings);
    
    // Check if settings that affect gameplay have changed and require a new game
    const gameplaySettingsChanged = newSettings.selectedWordPack !== undefined || 
                                   newSettings.rstlneMode !== undefined;
    
    if (gameMode === 'free-play' && gameplaySettingsChanged) {
      const newGameState = stateManager.startNewGame('free-play');
      setGameState(newGameState);
    }
  };

  // Keyboard event handlers for buttons
  const handleKeyDown = (event: React.KeyboardEvent, action: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  if (isLoading) {
    return (
      <div className={`app ${settings.darkMode ? 'dark-mode' : ''}`}>
        <div className="loading">
          <div className="loading-spinner"></div>
          <div>Loading game...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`app ${settings.darkMode ? 'dark-mode' : ''}`}>
        <div className="error-container">
          <div className="error-message">
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
            <button 
              className="error-retry-button"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className={`app ${settings.darkMode ? 'dark-mode' : ''}`}>
        <div className="loading">Initializing game...</div>
      </div>
    );
  }

  return (
    <div className={`app ${settings.darkMode ? 'dark-mode' : ''}`}>
      {/* Skip link for screen readers */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Live regions for screen reader announcements */}
      <div 
        aria-live="polite" 
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {announcement}
      </div>
      
      <div 
        aria-live="assertive" 
        aria-atomic="true"
        className="sr-only"
        role="alert"
      >
        {gameStatusAnnouncement}
      </div>

      <header className="app-header">
        <h1>RSTLNE</h1>
      </header>
      
      <nav className="game-controls" role="navigation" aria-label="Game controls">
        <div className="game-header-row">
          <button 
            className="mode-switch-link" 
            onClick={handleGameModeSwitch}
            onKeyDown={(e) => handleKeyDown(e, handleGameModeSwitch)}
            aria-describedby="mode-description"
          >
            Switch to {gameMode === 'daily' ? 'Free Play' : 'Daily'} Mode
          </button>
          <div id="mode-description" className="sr-only">
            Currently in {gameMode === 'daily' ? 'Daily Challenge' : 'Free Play'} mode
          </div>
          
          <div className="header-icons">
            <button 
              className="icon-button help-button" 
              onClick={() => {
                stateManager.trackModalOpen(MODAL_TYPES.HOW_TO_PLAY);
                setShowHowToPlay(true);
              }}
              onKeyDown={(e) => handleKeyDown(e, () => {
                stateManager.trackModalOpen(MODAL_TYPES.HOW_TO_PLAY);
                setShowHowToPlay(true);
              })}
              aria-label="Open how to play instructions"
              title="How to Play"
            >
              ❓
            </button>
            <button 
              className="icon-button settings-button" 
              onClick={() => {
                stateManager.trackModalOpen(MODAL_TYPES.SETTINGS);
                setShowSettings(true);
              }}
              onKeyDown={(e) => handleKeyDown(e, () => {
                stateManager.trackModalOpen(MODAL_TYPES.SETTINGS);
                setShowSettings(true);
              })}
              aria-label="Open game settings"
              title="Settings"
            >
              ⚙️
            </button>
          </div>
        </div>
        
        {(gameMode === 'free-play' || (gameMode === 'daily' && (gameState.gameOver || dailyState?.completed))) && (
          <button 
            className="new-game-button" 
            onClick={handleNewGame}
            onKeyDown={(e) => handleKeyDown(e, handleNewGame)}
            aria-describedby="new-game-description"
          >
            {gameMode === 'free-play' ? 'Next Word' : 
             dailyState?.completed ? 'Results' : 'Continue Next Word'}
          </button>
        )}
        {gameMode === 'free-play' && (
          <div id="new-game-description" className="sr-only">
            Start a new free play game with selected word pack
          </div>
        )}
      </nav>
      
      <main id="main-content" className="game-container" role="main">
        {showResults && dailyState ? (
          <Results 
            isOpen={showResults}
            dailyState={dailyState}
            persistentState={persistentState}
            darkMode={settings.darkMode}
            onClose={() => setShowResults(false)}
            stateManager={stateManager}
          />
        ) : (
          <div className="game-board-container" style={{ position: 'relative' }}>
            <GameBoard
              gameState={gameState}
              settings={settings}
              onGuessLetter={handleLetterGuess}
              dailyChallengeCompleted={gameMode === 'daily' && dailyState?.completed === true}
              gameMode={gameMode}
            />
            {settings.animationsEnabled && (
              <WordCelebration
                isActive={showCelebration}
                intensity={celebrationIntensity}
                onAnimationComplete={() => setShowCelebration(false)}
              />
            )}
          </div>
        )}
      </main>
      
      <StreakDisplay
        streak={persistentState.streak}
        streakScore={persistentState.streakScore}
        visible={gameMode === 'daily' && persistentState.streak > 0}
      />

      <Footer darkMode={settings.darkMode} />
      
      {/* Modal Components */}
      {showSettings && (
        <Settings 
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          isDarkMode={settings.darkMode}
          onDarkModeToggle={() => handleSettingsChange({ darkMode: !settings.darkMode })}
          selectedWordPack={settings.selectedWordPack}
          onSelectedWordPackChange={(packId: string) => handleSettingsChange({ selectedWordPack: packId })}
          isRstlneMode={settings.rstlneMode}
          onRstlneModeToggle={() => handleSettingsChange({ rstlneMode: !settings.rstlneMode })}
          animationsEnabled={settings.animationsEnabled}
          onAnimationsToggle={() => handleSettingsChange({ animationsEnabled: !settings.animationsEnabled })}
          gameMode={gameMode}
        />
      )}
      
      {showHowToPlay && (
        <HowToPlay 
          isOpen={showHowToPlay}
          onClose={() => setShowHowToPlay(false)}
        />
      )}
    </div>
  );
}

export default App;
